<template>
    <emc-alert-modal
        :show.sync="showModal"
        title="Sugerir Troca de Sala"
        icon="mdi-swap-horizontal"
        color="orange lighten-5"
        color-icon="orange"
        width="600"
        text-cancel="Cancelar"
        :persistent="false"
        @clickCancel="cancel"
    >
        <template v-slot:content>
            <v-form ref="form" v-model="valid" lazy-validation>
                <v-row>
                    <v-col cols="12">
                        <v-alert type="info" dense text class="mb-0">
                            Selecione a sala de destino para sugerir uma troca.
                            A troca será avaliada considerando a disponibilidade e capacidade.
                        </v-alert>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12" md="6">
                        <v-card outlined class="pa-3">
                            <div class="caption grey--text mb-1">Sala Atual</div>
                            <div class="subtitle-1 font-weight-medium">
                                {{ sourceAmbience ? sourceAmbience.name : '-' }}
                            </div>
                            <div class="body-2 grey--text" v-if="sourceAmbience">
                                Capacidade: {{ sourceAmbience.capacity || 0 }}
                            </div>
                            <v-chip
                                v-if="sourceOccupation"
                                x-small
                                :color="getOccupationColor(sourceAmbience, sourceOccupation)"
                                dark
                                class="mt-2"
                            >
                                Ocupação: {{ getOccupationPercentage(sourceAmbience, sourceOccupation) }}%
                            </v-chip>
                        </v-card>
                    </v-col>

                    <v-col cols="12" md="6">
                        <v-card outlined class="pa-3" :class="{ 'border-primary': targetAmbience }">
                            <div class="caption grey--text mb-1">Sala Destino</div>
                            <template v-if="targetAmbience">
                                <div class="subtitle-1 font-weight-medium">
                                    {{ targetAmbience.name }}
                                </div>
                                <div class="body-2 grey--text">
                                    Capacidade: {{ targetAmbience.capacity || 0 }}
                                </div>
                                <v-chip
                                    v-if="targetOccupation"
                                    x-small
                                    :color="getOccupationColor(targetAmbience, targetOccupation)"
                                    dark
                                    class="mt-2"
                                >
                                    Ocupação: {{ getOccupationPercentage(targetAmbience, targetOccupation) }}%
                                </v-chip>
                            </template>
                            <div v-else class="grey--text text--lighten-1">
                                Selecione abaixo
                            </div>
                        </v-card>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12">
                        <sys-ambience-search
                            v-model="targetAmbienceId"
                            label="Buscar sala de destino"
                            :rules="[rules.required]"
                            :filters="ambienceFilters"
                            :exclude-ids="[sourceAmbience ? sourceAmbience.id : null]"
                            @select="onSelectAmbience"
                            clearable
                            outlined
                            dense
                        />
                    </v-col>
                </v-row>

                <v-row v-if="targetAmbience">
                    <v-col cols="12">
                        <v-textarea
                            v-model="justification"
                            label="Justificativa (opcional)"
                            placeholder="Descreva o motivo da sugestão de troca..."
                            outlined
                            dense
                            rows="3"
                            counter="500"
                            :rules="[rules.maxLength]"
                        />
                    </v-col>
                </v-row>

                <v-row v-if="validationResult">
                    <v-col cols="12">
                        <v-alert
                            :type="validationResult.valid ? 'success' : 'warning'"
                            dense
                            text
                        >
                            <div class="font-weight-medium">{{ validationResult.message }}</div>
                            <ul v-if="validationResult.details && validationResult.details.length" class="mt-1 mb-0">
                                <li v-for="(detail, i) in validationResult.details" :key="i" class="body-2">
                                    {{ detail }}
                                </li>
                            </ul>
                        </v-alert>
                    </v-col>
                </v-row>
            </v-form>
        </template>

        <template v-slot:actions>
            <v-btn
                color="primary"
                :loading="loading"
                :disabled="!canSubmit"
                @click="submit"
            >
                <v-icon left>mdi-check</v-icon>
                Confirmar Troca
            </v-btn>
        </template>
    </emc-alert-modal>
</template>

<script>
import { getOccupationColor, getOccupationPercentage } from './helpers/occupationStatus'

export default {
    name: "SysAmbienceSuggestSwapModal",

    data: () => ({
        showModal: false,
        valid: false,
        loading: false,
        validating: false,
        
        targetAmbienceId: null,
        targetAmbience: null,
        targetOccupation: null,
        justification: '',
        validationResult: null,

        rules: {
            required: v => !!v || 'Campo obrigatório',
            maxLength: v => !v || v.length <= 500 || 'Máximo 500 caracteres'
        }
    }),

    props: {
        show: {
            type: Boolean,
            default: false
        },
        sourceAmbience: {
            type: Object,
            default: null
        },
        sourceOccupation: {
            type: Object,
            default: null
        },
        filters: {
            type: Object,
            default: () => ({})
        }
    },

    computed: {
        ambienceFilters() {
            return {
                campus_id: this.filters.campus_id,
                ...this.filters,
                active: true
            }
        },

        canSubmit() {
            return this.valid && 
                   this.targetAmbience && 
                   this.sourceAmbience && 
                   !this.loading &&
                   (!this.validationResult || this.validationResult.valid)
        }
    },

    watch: {
        show(newVal) {
            this.showModal = newVal
            if (newVal) {
                this.reset()
            }
        },
        showModal(newVal) {
            this.$emit('update:show', newVal)
        },
        targetAmbienceId(newVal) {
            if (!newVal) {
                this.targetAmbience = null
                this.targetOccupation = null
                this.validationResult = null
            }
        }
    },

    methods: {
        reset() {
            this.targetAmbienceId = null
            this.targetAmbience = null
            this.targetOccupation = null
            this.justification = ''
            this.validationResult = null
            if (this.$refs.form) {
                this.$refs.form.resetValidation()
            }
        },

        cancel() {
            this.showModal = false
            this.$emit('cancel')
        },

        async onSelectAmbience(ambience) {
            this.targetAmbience = ambience
            if (ambience) {
                await this.validateSwap()
                await this.fetchTargetOccupation()
            }
        },

        async fetchTargetOccupation() {
            if (!this.targetAmbience) return

            try {
                const response = await this.$http.get(`/ambiences/${this.targetAmbience.id}/occupation`, {
                    params: this.filters
                })
                this.targetOccupation = response.data
            } catch (error) {
                this.$emit('error', error)
            }
        },

        async validateSwap() {
            if (!this.sourceAmbience || !this.targetAmbience) return

            this.validating = true
            this.validationResult = null

            try {
                const response = await this.$http.post('/room-allocation/validate-swap', {
                    source_ambience_id: this.sourceAmbience.id,
                    target_ambience_id: this.targetAmbience.id,
                    ...this.filters
                })
                this.validationResult = response.data
            } catch (error) {
                this.validationResult = {
                    valid: false,
                    message: 'Erro ao validar troca',
                    details: [error.message || 'Tente novamente mais tarde']
                }
            } finally {
                this.validating = false
            }
        },

        async submit() {
            if (!this.$refs.form.validate()) return

            this.loading = true

            try {
                const payload = {
                    source_ambience_id: this.sourceAmbience.id,
                    target_ambience_id: this.targetAmbience.id,
                    justification: this.justification,
                    ...this.filters
                }

                await this.$http.post('/room-allocation/suggest-swap', payload)

                this.$emit('success', {
                    source: this.sourceAmbience,
                    target: this.targetAmbience
                })

                this.showModal = false
            } catch (error) {
                this.$emit('error', error)
            } finally {
                this.loading = false
            }
        },

        getOccupationPercentage,
        getOccupationColor
    }
}
</script>

<style scoped>
.border-primary {
    border-color: var(--v-primary-base) !important;
    border-width: 2px !important;
}
</style>
